/**
 * Leer códigos con la cámara en ANDROID (2026-11-04).
 *
 * El porqué está en `lecturaDeCodigos.ts`, en «Android leía peor que iPhone».
 * Aquí está el cómo: abrir la lente que enfoca, pedirle zoom y leer con dos
 * motores a la vez.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ DOS MOTORES, Y NO EL DE ANDROID SOLO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El lector de Android (`BarcodeDetector`) es mucho mejor con una imagen borrosa,
 * pero hay aparatos donde dice estar y no responde: teléfonos sin los servicios
 * de Google, o con su módulo todavía sin descargar. Ahí no hay error que atrapar,
 * simplemente nunca encuentra nada.
 *
 * Por eso el lector de siempre (zxing) sigue trabajando detrás, menos seguido.
 * Así, en el peor caso este camino lee como el anterior, nunca peor.
 *
 * Sin el de Android, zxing se queda solo: mira solo la mirilla (menos trabajo y
 * sin el brillo de alrededor, medido: de 81 a 96 % con reflejo) y en un intento
 * de cada dos busca a fondo (`TRY_HARDER`), también con el código de lado.
 */

import {
  ajustesParaLeer,
  type Candidato,
  type Capacidades,
  confirmarLectura,
  elegirDetectado,
  enfocaSola,
  FORMATOS,
  formatosNativos,
  MARGEN_AL_ELEGIR,
  MARGEN_AL_RECORTAR,
  tieneDigitoDeControl,
  zonaDeLaMirilla,
  type Zona,
} from "@/lib/lecturaDeCodigos";

/** Lo que encontró un motor en un cuadro. */
export type Hallazgo = {
  codigo: string;
  /** Si no, necesita verse dos veces. Ver `confirmarLectura`. */
  fiable: boolean;
  motor: "android" | "zxing";
};

/** Un motor de lectura: mira el cuadro de ahora. */
export type Motor = (turno: number) => Promise<Hallazgo | null> | Hallazgo | null;

/** Lo que se muestra en `/prueba-del-lector`, para saber qué hizo cada teléfono. */
export type Diagnostico = {
  motor: string;
  camara: string;
  resolucion: string;
  enfoque: string;
  zoom: string;
  ultimoMotor?: Hallazgo["motor"];
};

export type SesionDeLectura = {
  pista: MediaStreamTrack | null;
  capacidades: Capacidades;
  /** Lo pedido a la cámara: la linterna lo repite para no perder el zoom. */
  ajustes: Record<string, unknown>[];
  /** Tocar la pantalla: enfoca ese punto. `x` e `y` van de 0 a 1. */
  enfocar: (x: number, y: number) => void;
  detener: () => void;
};

/** Cada cuánto zxing acompaña al lector de Android: un cuadro de cada cuatro. */
const ZXING_CADA = 4;
/** La pausa entre cuadros. Sin esto, el teléfono se calienta sin leer más. */
const PAUSA_CON_ANDROID = 80;
const PAUSA_SOLO_ZXING = 150;
/** Más ancho no lee más, y cada píxel de más es tiempo con la pantalla parada. */
const ANCHO_PARA_ZXING = 800;

const esperar = (ms: number) => new Promise((resolver) => setTimeout(resolver, ms));

/**
 * La vuelta de lectura. Aparte y sin cámara para poder probarla.
 *
 * ⚠️ El siguiente cuadro se pide cuando termina este, nunca con un `setInterval`:
 * en un teléfono lento, las lecturas se montarían unas encima de otras.
 */
export function bucleDeLectura(op: {
  android: Motor | null;
  zxing: Motor | null;
  listo: () => boolean;
  alHallar: (h: Hallazgo) => void;
}): () => void {
  let vivo = true;
  let android = op.android;
  let turno = 0;
  let temporizador: ReturnType<typeof setTimeout> | undefined;

  const paso = async () => {
    if (!vivo) return;
    if (op.listo()) {
      turno += 1;
      let hallado: Hallazgo | null = null;

      if (android) {
        try {
          hallado = await android(turno);
        } catch {
          // Este aparato no lo tiene de verdad: para esta sesión, queda zxing.
          android = null;
        }
      }
      if (vivo && !hallado && op.zxing && (!android || turno % ZXING_CADA === 0)) {
        try {
          hallado = await op.zxing(turno);
        } catch {
          // Un cuadro que no se pudo mirar no detiene la cámara.
        }
      }
      if (vivo && hallado) op.alHallar(hallado);
    }
    if (vivo) temporizador = setTimeout(() => void paso(), android ? PAUSA_CON_ANDROID : PAUSA_SOLO_ZXING);
  };

  void paso();
  return () => {
    vivo = false;
    clearTimeout(temporizador);
  };
}

/** Lo que devuelve el lector de Android (no viene en los tipos de TypeScript). */
type CodigoDetectado = {
  rawValue: string;
  format: string;
  boundingBox: { x: number; y: number; width: number; height: number };
};
type DetectorNativo = { detect: (fuente: HTMLVideoElement) => Promise<CodigoDetectado[]> };
type ClaseDetector = {
  new (opciones: { formats: string[] }): DetectorNativo;
  getSupportedFormats: () => Promise<string[]>;
};

/** El lector de Android, o `null` si este aparato no lo tiene de verdad. */
async function crearDetectorNativo(): Promise<DetectorNativo | null> {
  const Clase = (window as unknown as { BarcodeDetector?: ClaseDetector }).BarcodeDetector;
  if (!Clase?.getSupportedFormats) return null;
  try {
    const formatos = formatosNativos(await Clase.getSupportedFormats());
    return formatos.length ? new Clase({ formats: formatos }) : null;
  } catch {
    return null;
  }
}

async function crearMotorZxing(video: HTMLVideoElement, zona: () => Zona, conBusquedaAFondo: () => boolean) {
  const [{ HTMLCanvasElementLuminanceSource }, { BarcodeFormat, BinaryBitmap, DecodeHintType, HybridBinarizer, MultiFormatReader }] =
    await Promise.all([import("@zxing/browser"), import("@zxing/library")]);

  const lector = (aFondo: boolean) => {
    const r = new MultiFormatReader();
    const pistas = new Map<number, unknown>([[DecodeHintType.POSSIBLE_FORMATS, FORMATOS.map((f) => BarcodeFormat[f])]]);
    if (aFondo) pistas.set(DecodeHintType.TRY_HARDER, true);
    r.setHints(pistas as never);
    return r;
  };
  const normal = lector(false);
  const aFondo = lector(true);

  const lienzo = document.createElement("canvas");
  const dibujo = lienzo.getContext("2d", { willReadFrequently: true });

  const motor: Motor = (turno) => {
    if (!dibujo) return null;
    const z = zona();
    const escala = Math.min(1, ANCHO_PARA_ZXING / z.w);
    const ancho = Math.round(z.w * escala);
    const alto = Math.round(z.h * escala);
    // Cambiar el tamaño de un lienzo lo vuelve a crear: solo cuando haga falta.
    if (lienzo.width !== ancho || lienzo.height !== alto) {
      lienzo.width = ancho;
      lienzo.height = alto;
    }
    dibujo.drawImage(video, z.x, z.y, z.w, z.h, 0, 0, ancho, alto);

    const imagen = new BinaryBitmap(new HybridBinarizer(new HTMLCanvasElementLuminanceSource(lienzo)));
    const lectorDeAhora = conBusquedaAFondo() && turno % 2 === 0 ? aFondo : normal;
    try {
      return { codigo: lectorDeAhora.decodeWithState(imagen).getText(), fiable: false, motor: "zxing" };
    } catch {
      return null; // Lo normal: en este cuadro no hay código que leer.
    }
  };
  return motor;
}

const CLAVE_DE_LA_LENTE = "softfi.camara.lente";
/** Ya se buscó y ninguna lente enfocaba mejor: no volver a buscar. */
const LENTE_PREDETERMINADA = "predeterminada";

function lenteGuardada(): string | null {
  try {
    return localStorage.getItem(CLAVE_DE_LA_LENTE);
  } catch {
    return null;
  }
}

function guardarLente(valor: string | null) {
  try {
    if (valor === null) localStorage.removeItem(CLAVE_DE_LA_LENTE);
    else localStorage.setItem(CLAVE_DE_LA_LENTE, valor);
  } catch {
    /* Ventana privada: se vuelve a buscar la próxima vez. */
  }
}

/**
 * Lo que la cámara sabe hacer.
 *
 * ⚠️ Chrome puede tardar un instante en contestarlo tras abrir la cámara: leído
 * de inmediato, algún aparato parecería no tener zoom ni linterna.
 */
async function esperarCapacidades(pista: MediaStreamTrack): Promise<Capacidades> {
  // Firefox no la tiene: esperar solo retrasaría el encendido sin ganar nada.
  if (!pista.getCapabilities) return {};
  for (let i = 0; i < 6; i++) {
    const c = pista.getCapabilities() as Capacidades;
    if (enfocaSola(c) !== null) return c;
    await esperar(150);
  }
  return pista.getCapabilities() as Capacidades;
}

/**
 * Abre la cámara de atrás, la que enfoca.
 *
 * ⚠️ Con varias cámaras atrás, «la de atrás» no siempre es la principal: en
 * algunos Android abre la gran angular, de enfoque fijo, que de cerca nunca ve
 * nítido. Eso se detecta en lo que la lente dice de sí misma (las etiquetas no
 * sirven, cada fabricante pone otra cosa) y se prueba con las demás. Solo pasa
 * la primera vez en ese teléfono: la elegida queda guardada.
 *
 * 1080p y no 720p: con el teléfono algo más lejos, el código sigue teniendo los
 * píxeles que necesita.
 */
async function abrirCamaraTrasera(): Promise<MediaStream> {
  const calidad = { width: { ideal: 1920 }, height: { ideal: 1080 } };
  const pedir = (video: MediaTrackConstraints) => navigator.mediaDevices.getUserMedia({ video: { ...video, ...calidad } });
  const laDeAtras = () => pedir({ facingMode: { ideal: "environment" } });

  const guardada = lenteGuardada();
  if (guardada === LENTE_PREDETERMINADA) return laDeAtras();
  if (guardada) {
    try {
      return await pedir({ deviceId: { exact: guardada } });
    } catch {
      guardarLente(null); // Ya no existe: tras borrar los datos del sitio, cambia.
    }
  }

  const stream = await laDeAtras();
  const pista = stream.getVideoTracks()[0];
  if (!pista || enfocaSola(await esperarCapacidades(pista)) !== false) return stream;

  // Las demás lentes solo se ven con la cámara ya permitida. Y solo se puede
  // abrir una a la vez: hay que soltar esta antes de probar la siguiente.
  const actual = pista.getSettings().deviceId;
  const dispositivos = await navigator.mediaDevices.enumerateDevices();
  const otras = dispositivos
    .filter((d) => d.kind === "videoinput" && d.deviceId && d.deviceId !== actual && !/front|frontal|user/i.test(d.label))
    .slice(0, 4);
  pista.stop();

  for (const otra of otras) {
    try {
      const prueba = await pedir({ deviceId: { exact: otra.deviceId } });
      const suPista = prueba.getVideoTracks()[0];
      const c = suPista ? await esperarCapacidades(suPista) : {};
      if (!c.facingMode?.includes("user") && enfocaSola(c) === true) {
        guardarLente(otra.deviceId);
        return prueba;
      }
      prueba.getTracks().forEach((t) => t.stop());
    } catch {
      /* Esa lente no abre sola: se sigue con la siguiente. */
    }
  }

  guardarLente(LENTE_PREDETERMINADA);
  return laDeAtras();
}

/** Aplica los ajustes juntos y, si la cámara los rechaza, uno por uno. */
async function aplicar(pista: MediaStreamTrack, ajustes: Record<string, unknown>[]) {
  if (!ajustes.length) return;
  try {
    await pista.applyConstraints({ advanced: ajustes as MediaTrackConstraintSet[] });
  } catch {
    for (const ajuste of ajustes) {
      try {
        await pista.applyConstraints({ advanced: [ajuste as MediaTrackConstraintSet] });
      } catch {
        // Hay aparatos que anuncian lo que luego no dan: se lee con lo que haya.
      }
    }
  }
}

/**
 * Enciende la cámara en Android y empieza a leer.
 *
 * Devuelve `null` si el escáner se cerró mientras encendía.
 */
export async function leerEnAndroid(op: {
  video: HTMLVideoElement;
  mirilla: () => HTMLElement | null;
  alLeer: (codigo: string) => void;
  sigueAbierto: () => boolean;
  alDiagnostico?: (d: Diagnostico) => void;
}): Promise<SesionDeLectura | null> {
  const { video } = op;

  /** La mirilla en el cuadro de la cámara. Se mide cada vez: el teléfono gira. */
  const zona = (margen: number) =>
    zonaDeLaMirilla(
      { ancho: video.videoWidth, alto: video.videoHeight },
      video.getBoundingClientRect(),
      op.mirilla()?.getBoundingClientRect() ?? null,
      margen,
    );

  const detector = await crearDetectorNativo();
  const android: Motor | null = detector
    ? async () => {
        const elegido = elegirDetectado(await detector.detect(video), zona(MARGEN_AL_ELEGIR));
        return elegido
          ? { codigo: elegido.rawValue, fiable: tieneDigitoDeControl(elegido.format), motor: "android" }
          : null;
      }
    : null;

  // Si el archivo de zxing no se pudo cargar, con el de Android se lee igual.
  const zxing = await crearMotorZxing(video, () => zona(MARGEN_AL_RECORTAR), () => !detector).catch(() => null);
  if (!android && !zxing) throw new Error("Sin motor de lectura");

  const stream = await abrirCamaraTrasera();
  let detenerBucle: (() => void) | null = null;
  const detener = () => {
    detenerBucle?.();
    stream.getTracks().forEach((t) => t.stop());
    // Solo si sigue siendo el suyo: al reabrir rápido ya puede haber otro.
    if (video.srcObject === stream) video.srcObject = null;
  };

  if (!op.sigueAbierto()) {
    detener();
    return null;
  }

  video.srcObject = stream;
  await video.play().catch(() => undefined);

  const pista = stream.getVideoTracks()[0] ?? null;
  const capacidades = pista ? await esperarCapacidades(pista) : {};
  const ajustes = ajustesParaLeer(capacidades);
  if (pista) await aplicar(pista, ajustes);

  if (!op.sigueAbierto()) {
    detener();
    return null;
  }

  const ajustesDe = pista?.getSettings() as (MediaTrackSettings & { zoom?: number }) | undefined;
  const diagnostico: Diagnostico = {
    motor: android ? "Lector de Android" : "zxing (sin lector de Android)",
    camara: pista?.label || "Sin nombre",
    resolucion: `${ajustesDe?.width ?? "?"} × ${ajustesDe?.height ?? "?"}`,
    enfoque: capacidades.focusMode?.join(", ") || "No informa",
    zoom: capacidades.zoom ? `${ajustesDe?.zoom ?? "?"} (máximo ${capacidades.zoom.max ?? "?"})` : "No disponible",
  };
  op.alDiagnostico?.(diagnostico);

  let candidato: Candidato | null = null;
  detenerBucle = bucleDeLectura({
    android,
    zxing,
    listo: () => video.readyState >= 2 && video.videoWidth > 0 && !document.hidden,
    alHallar: (h) => {
      if (!h.fiable) {
        const r = confirmarLectura(h.codigo, candidato, Date.now());
        candidato = r.candidato;
        if (!r.confirmada) return;
      }
      if (diagnostico.ultimoMotor !== h.motor) {
        diagnostico.ultimoMotor = h.motor;
        op.alDiagnostico?.({ ...diagnostico });
      }
      op.alLeer(h.codigo);
    },
  });

  // Solo en el aparato que sabe enfocar a un punto; en los demás, tocar no hace nada.
  let volverAlEnfoque: ReturnType<typeof setTimeout> | undefined;
  const enfocar = (x: number, y: number) => {
    if (!pista || !capacidades.focusMode?.includes("single-shot")) return;
    clearTimeout(volverAlEnfoque);
    void aplicar(pista, [...ajustes, { pointsOfInterest: [{ x, y }] }, { focusMode: "single-shot" }]);
    volverAlEnfoque = setTimeout(() => {
      if (pista.readyState === "live") void aplicar(pista, ajustes);
    }, 1_500);
  };

  return {
    pista,
    capacidades,
    ajustes,
    enfocar,
    detener: () => {
      clearTimeout(volverAlEnfoque);
      detener();
    },
  };
}
